import React from "react";
import { Chip, ChipProps } from "@shared/src/components/ui/chip";
import { TextWithCopy } from "@shared/src/components/common/misc/TextWithCopy";
import { Token } from "@sdk/shared/types/config.types";
import { useHostStore } from "@shared/src/stores/useHostStore";
export interface TokenChipProps extends Omit<ChipProps, "children"> {
  showCopy?: boolean;
  iconUrl?: string;
  symbol?: string;
}

// Ensure at least symbol or tokenAddress is provided
export type ValidatedTokenChipProps =
  | (TokenChipProps & {
      tokenAddress: string;
      networkId: string | number;
    })
  | (TokenChipProps & { symbol: string; showCopy?: undefined | false })
  | (TokenChipProps & { showCopy: true; networkId: string | number; symbol: string })
  | (TokenChipProps & { showCopy: true; networkId: string | number; tokenAddress: string });

export function TokenChip(props: ValidatedTokenChipProps) {
  const sdk = useHostStore(state => state.sdk);
  const tokens = sdk?.config.tokens;

  if (!tokens) {
    return <></>;
  }
  const tokenDetails = getTokenDetails(props, tokens);

  function getTokenDetails(props: ValidatedTokenChipProps, tokens: Token[]) {
    // Case 1: Looking up by token address
    if ("tokenAddress" in props) {
      const hasNetworkId = "networkId" in props;
      return tokens.find(token => {
        const addressMatches = token.address.toLowerCase() === props.tokenAddress?.toLowerCase();
        if (!hasNetworkId) return addressMatches;
        return addressMatches && Number(props.networkId) === token.networkId;
      });
    }

    // Case 2: Looking up by symbol and network ID
    else if ("networkId" in props) {
      return tokens.find(token => token.symbol === props.symbol && Number(props.networkId) === token.networkId);
    }

    // Case 3: Fallback to provided values
    else
      return {
        symbol: props.symbol,
        icon: props.iconUrl || tokens.find(t => t.symbol === props.symbol)?.icon,
        address: undefined,
      };
  }

  if (!tokenDetails?.symbol) {
    console.error({ props, tokenDetails, tokens });
    throw "Token not found";
  }

  const { tokenAddress, symbol, iconUrl, networkId, ...chipProps } = props as ValidatedTokenChipProps & {
    networkId?: string | number;
    tokenAddress?: string;
  };

  return (
    <Chip icon={tokenDetails?.icon} iconAlt={`${tokenDetails.symbol} icon`} className={props.className} {...chipProps}>
      {props.showCopy ? (
        <TextWithCopy
          shorten={false}
          differentTextToCopy={tokenDetails.address}
          text={tokenDetails?.symbol}
          toastMessage="Token address copied to clipboard"
        />
      ) : (
        <span className="truncate">{tokenDetails.symbol}</span>
      )}
    </Chip>
  );
}
